import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { apiUrl } from '../utils/api';
import InlineLoader from '../components/InlineLoader';

const STUDIES = [
  { value: 'BRUCELOSIS', label: 'Brucelosis', fields: ['bpa', 'fpa'] },
  { value: 'BVD', label: 'Diarrea Viral Bovina', fields: ['elisa_p80_p125'] },
  { value: 'CAMPYLOBACTER', label: 'Campylobacter fetus', fields: ['qpcr_campylobacter'] },
  { value: 'TRITRICHOMONAS', label: 'Tritrichomonas foetus', fields: ['qpcr_tritrichomonas'] },
];
const COLUMNS = [
  ['bpa', 'BPA'], ['fpa', 'FPA'], ['elisa_p80_p125', 'ELISA p80/p125'],
  ['qpcr_campylobacter', 'qPCR Campylobacter fetus spp'],
  ['qpcr_tritrichomonas', 'qPCR Tritrichomonas foetus'],
];
const headers = () => ({ Authorization: `Token ${localStorage.getItem('token') || ''}` });
const blankRow = (number) => ({ numero_orden: number, identificacion: '', bpa: '', fpa: '', elisa_p80_p125: '', qpcr_campylobacter: '', qpcr_tritrichomonas: '' });

export default function PackToroPage() {
  const { protocolo } = useParams();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [casesError, setCasesError] = useState('');
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [study, setStudy] = useState('BRUCELOSIS');
  const [saving, setSaving] = useState(false);
  const editable = useMemo(() => STUDIES.find((item) => item.value === study)?.fields || [], [study]);

  useEffect(() => {
    if (protocolo) {
      setLoadingCases(false);
      return;
    }

    setLoadingCases(true);
    setCasesError('');
    axios.get(apiUrl('/api/ingresos_casos/?compact=1&include_case_labels=1&tipo_estudio=PACK_TORO'), { headers: headers() })
      .then(({ data: response }) => setCases(Array.isArray(response) ? response : []))
      .catch(() => {
        setCases([]);
        setCasesError('No se pudieron cargar los casos Pack toro.');
      })
      .finally(() => setLoadingCases(false));
  }, [protocolo]);

  useEffect(() => {
    if (!protocolo) return;
    axios.get(apiUrl(`/api/pack-toro/${protocolo}/`), { headers: headers() })
      .then(({ data: response }) => {
        setData(response);
        setRows(response.filas.length ? response.filas : [blankRow(1)]);
      })
      .catch((error) => toast.error(error.response?.data?.detail || 'No se pudo abrir el pack toro.'));
  }, [protocolo]);

  const updateRow = (index, field, value) => setRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row));
  const addRow = () => setRows((current) => [...current, blankRow(current.length + 1)]);
  const removeLast = () => setRows((current) => current.length > 1 ? current.slice(0, -1) : current);

  const save = async () => {
    if (rows.some((row) => !row.identificacion.trim())) {
      toast.error('Completa la identificación de todas las muestras.'); return;
    }
    setSaving(true);
    try {
      const response = await axios.post(apiUrl(`/api/pack-toro/${protocolo}/`), { estudio: study, filas: rows }, { headers: headers() });
      setData(response.data); setRows(response.data.filas);
      toast.success('Estudio guardado. Las demás columnas no fueron modificadas.');
    } catch (error) {
      const responseData = error.response?.data;
      const isStructuredError = responseData && typeof responseData === 'object' && !Array.isArray(responseData);
      const message = isStructuredError
        ? Object.values(responseData).flat().join(' ')
        : 'No se pudo guardar el estudio. Revisa que el backend esté actualizado e intenta nuevamente.';
      toast.error(message);
    } finally { setSaving(false); }
  };

  if (!protocolo) return (
    <div className="surface-card">
      <span className="section-kicker">Sector</span><h2 className="form-title">Pack toro</h2>
      <p className="text-muted">Selecciona un caso para completar su tabla de resultados.</p>
      {loadingCases ? (
        <InlineLoader label="Cargando casos Pack toro..." />
      ) : casesError ? (
        <div className="alert subtle-alert mb-0" role="alert">{casesError}</div>
      ) : cases.length === 0 ? (
        <div className="dashboard-empty dashboard-empty--compact">No hay casos Pack toro disponibles.</div>
      ) : (
        <select className="form-select" value="" onChange={(event) => event.target.value && navigate(`/pack-toro/${event.target.value}`)}>
          <option value="">Seleccionar protocolo</option>
          {cases.map((item) => <option key={item.protocolo_general} value={item.protocolo_general}>{item.protocolo_general} - {item.propietario || 'Sin propietario'}</option>)}
        </select>
      )}
    </div>
  );

  if (!data) return <div className="surface-card"><InlineLoader label="Cargando pack toro..." /></div>;

  return (
    <div className="surface-card">
      <div className="form-highlight mb-4"><div><span className="section-kicker">Sector</span><h2 className="form-title">Pack toro</h2><p className="mb-0">{data.propietario || 'Sin propietario'}</p></div><div className="d-flex flex-wrap align-items-center gap-2"><div className="protocol-chip">{protocolo}</div><button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/pack-toro')}>Cambiar protocolo</button><button type="button" className="btn btn-outline-brand" onClick={() => navigate(`/ver-caso/${protocolo}`)}>Ver caso</button></div></div>
      <div className="row g-3 align-items-end mb-4"><div className="col-md-7"><label className="form-label">Estudio que vas a cargar</label><select className="form-select" value={study} onChange={(e) => setStudy(e.target.value)}>{STUDIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select><div className="pack-toro-active-study mt-2"><span className="pack-toro-active-study__dot" />Carga activa: <strong>{STUDIES.find((item) => item.value === study)?.label}</strong></div></div><div className="col-md-5 text-md-end"><button type="button" className="btn btn-outline-brand me-2" onClick={addRow}>Agregar animal</button><button type="button" className="btn btn-outline-secondary" onClick={removeLast}>Quitar última fila</button></div></div>
      <div className="table-responsive"><table className="table table-bordered align-middle"><thead><tr><th>N°</th><th>Muestra / animal</th>{COLUMNS.map(([key, label]) => <th key={key} className={editable.includes(key) ? 'pack-toro-column--active' : 'pack-toro-column--inactive'}>{label}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={row.id || index}><td>{row.numero_orden}</td><td><input className="form-control" value={row.identificacion} onChange={(e) => updateRow(index, 'identificacion', e.target.value)} /></td>{COLUMNS.map(([key]) => <td key={key} className={editable.includes(key) ? 'pack-toro-cell--active' : 'pack-toro-cell--inactive'}><input className={`form-control ${editable.includes(key) ? 'pack-toro-result--active' : 'pack-toro-result--inactive'}`} value={row[key] || ''} readOnly={!editable.includes(key)} onChange={(e) => updateRow(index, key, e.target.value)} placeholder={editable.includes(key) ? 'Resultado' : 'Otro estudio'} /></td>)}</tr>)}</tbody></table></div>
      <div className="mt-3 d-flex justify-content-between align-items-center"><div className="text-muted small">{data.cargas[study] ? `Última carga: ${data.cargas[study].operario}` : 'Este estudio todavía no fue cargado.'}</div><button type="button" className="btn btn-brand" disabled={saving} onClick={save}>{saving ? 'Guardando...' : 'Guardar mi estudio'}</button></div>
    </div>
  );
}